// Serviço de "Rota Inteligente" (spec Bruno — Unificação Calendário + Rota Inteligente).
//
// A rota é um artefato derivado das visitas planejadas (tarefas): a partir das paradas
// geolocalizadas do dia e do GPS do usuário, gera a ordem otimizada (TSP nearest-neighbor
// + 2-opt via Google Routes API, com fallback local) e persiste rotas_comerciais +
// rota_paradas. Reutilizado por vendedor, parceiro e motoboy (OperadorView).

import { supabase } from './supabase';
import { optimizeRoute, googleMapsDirLink, googleMapsNavUrl, GOOGLE_MAPS_MAX_STOPS, type RoutePoint } from './routeOptimizer';

export interface PontoRoteirizavel extends RoutePoint {
    id: string;             // String(tarefa_id) — usado como id do otimizador
    negociacao_id: number;
    tarefa_id: number;
}

export interface Origem { lat: number; lng: number }

export interface ResultadoOtimizacao<T extends RoutePoint = PontoRoteirizavel> {
    order: T[];
    totalKm: number;
    duracaoMin: number | null;
    fonte: 'google' | 'local';
}

/** Data de hoje em ISO local (evita o "pula pra amanhã" do toISOString() em UTC à noite). */
export const hojeLocalIso = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Localização atual do usuário. Nunca rejeita: resolve undefined se negado/indisponível. */
export const getOrigin = (timeoutMs = 5000): Promise<Origem | undefined> =>
    new Promise(resolve => {
        if (!navigator.geolocation) return resolve(undefined);
        navigator.geolocation.getCurrentPosition(
            pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => resolve(undefined),
            { timeout: timeoutMs },
        );
    });

/**
 * Otimiza a ordem das paradas. Tenta a Google Routes API (edge `otimizar-rota`);
 * cai no otimizador local em erro ou quando há mais de 24 pontos (limite da API).
 *
 * Genérica no tipo da parada porque a rota de COBRANÇA (src/lib/rotaCobranca.ts)
 * roteia unidades consumidoras, que não têm negociação nem tarefa. O que o
 * otimizador precisa é só `id`, `lat` e `lng` — o resto do objeto volta intacto.
 */
export const otimizarPontos = async <T extends RoutePoint>(
    pontos: T[],
    origin: Origem | undefined,
    origemLog: string,
    /** Ponto de chegada (opcional). Sem ele a rota termina na última parada. */
    destino?: Origem | null,
): Promise<ResultadoOtimizacao<T>> => {
    const local = (): ResultadoOtimizacao<T> => {
        const r = optimizeRoute(pontos, origin, destino);
        return { order: r.order, totalKm: r.totalKm, duracaoMin: null, fonte: 'local' };
    };
    if (pontos.length > 24) return local();
    try {
        const { data, error } = await supabase.functions.invoke('otimizar-rota', {
            body: {
                pontos: pontos.map(p => ({ id: p.id, lat: p.lat, lng: p.lng })),
                origin: origin || null,
                destino: destino || null,
                origem: origemLog,
            },
        });
        if (error || !data?.orderIds) throw error || new Error('Routes sem resultado');
        // Versão antiga da edge ignora `destino` e termina no ponto mais distante.
        // Sem a confirmação, a ordem dela não serve — o cálculo local respeita a chegada.
        if (destino && data.destino_respeitado !== true) throw new Error('Edge sem suporte a ponto de chegada');
        const byId = new Map(pontos.map(p => [p.id, p]));
        const ordenados = (data.orderIds as string[]).map(id => byId.get(id)).filter(Boolean) as T[];
        if (ordenados.length !== pontos.length) throw new Error('Ordem incompleta');
        return { order: ordenados, totalKm: Number(data.distanciaKm || 0), duracaoMin: data.duracaoMin ?? null, fonte: data.fonte === 'google' ? 'google' : 'local' };
    } catch (apiErr) {
        console.warn('Routes API indisponível, usando otimização local:', apiErr);
        return local();
    }
};

const round1 = (n: number) => Math.round(n * 10) / 10;

const montarSnapshot = (res: ResultadoOtimizacao, origin: Origem | undefined, versao: number) => ({
    versao,
    gerada_em: new Date().toISOString(),
    fonte: res.fonte,
    origin: origin ? { lat: origin.lat, lng: origin.lng } : null,
    total_km: round1(res.totalKm),
    duracao_min: res.duracaoMin,
    ordem: res.order.map((p, i) => ({ posicao: i + 1, tarefa_id: p.tarefa_id, negociacao_id: p.negociacao_id, lat: p.lat, lng: p.lng })),
});

const inserirParadas = async (rotaId: string, order: PontoRoteirizavel[]) => {
    const { error } = await supabase.from('rota_paradas').insert(
        order.map((p, i) => ({ rota_id: rotaId, posicao: i + 1, negociacao_id: p.negociacao_id, tarefa_id: p.tarefa_id, modalidade: 'presencial' })),
    );
    if (error) throw error;
};

export interface GerarResultado {
    rotaId: string;
    substituiu: boolean;
    totalKm: number;
    duracaoMin: number | null;
    fonte: 'google' | 'local';
}

/** Rota ativa (não concluída/cancelada) do responsável no dia, se houver — qualquer origem. */
async function buscarRotaAtivaDoDia(responsavelId: string, data: string) {
    const { data: rota } = await supabase
        .from('rotas_comerciais')
        .select('id, status, gerada_em')
        .eq('vendedor_id', responsavelId)
        .eq('data', data)
        .in('status', ['planejada', 'agendada', 'reagendada', 'em_execucao'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
    return rota as { id: string; status: string; gerada_em: string | null } | null;
}

/**
 * Gera (ou recalcula) a rota inteligente de um responsável num dia.
 * NUNCA cria uma segunda rota para o mesmo dia (adendo Bruno §1): se já existe
 * rota ativa — de QUALQUER origem — ela é atualizada no lugar (nada de criar
 * nova e cancelar a antiga). Em execução, as visitas novas são anexadas.
 */
export async function gerarRotaInteligente(args: {
    responsavelId: string;
    data: string;
    pontos: PontoRoteirizavel[];
    origin?: Origem;
}): Promise<GerarResultado> {
    const { responsavelId, data, pontos, origin } = args;
    if (pontos.length === 0) throw new Error('Nenhuma parada geolocalizada para roteirizar.');

    const reutilizar = async (rota: { id: string; status: string; gerada_em: string | null }): Promise<GerarResultado> => {
        if (rota.status === 'em_execucao') {
            await anexarERecalcularPendentes({ rotaId: rota.id, novosPontos: pontos, origin });
            const { data: atual } = await supabase.from('rotas_comerciais')
                .select('distancia_total_km, duracao_min').eq('id', rota.id).maybeSingle();
            return { rotaId: rota.id, substituiu: true, totalKm: Number(atual?.distancia_total_km || 0), duracaoMin: atual?.duracao_min ?? null, fonte: 'local' };
        }
        const r = await recalcularRotaPlanejada({ rotaId: rota.id, pontos, origin, geradaEmLida: rota.gerada_em });
        return { rotaId: rota.id, substituiu: true, totalKm: r.totalKm, duracaoMin: r.duracaoMin, fonte: r.fonte };
    };

    const existente = await buscarRotaAtivaDoDia(responsavelId, data);
    if (existente?.id) return reutilizar(existente);

    const res = await otimizarPontos(pontos, origin, 'inteligente');
    const { data: rota, error: rotaErr } = await supabase
        .from('rotas_comerciais')
        .insert([{
            vendedor_id: responsavelId,
            data,
            origem: 'inteligente',
            status: 'planejada',
            distancia_total_km: round1(res.totalKm),
            duracao_min: res.duracaoMin,
            gerada_em: new Date().toISOString(),
            snapshot_otimizacao: montarSnapshot(res, origin, 1),
        }])
        .select('id')
        .single();

    // Corrida/clique duplo: o unique index disparou -> rebusca e atualiza no lugar.
    if (rotaErr) {
        if ((rotaErr as { code?: string }).code === '23505') {
            const corrida = await buscarRotaAtivaDoDia(responsavelId, data);
            if (corrida?.id) return reutilizar(corrida);
        }
        throw rotaErr;
    }

    await inserirParadas(rota.id, res.order);
    return { rotaId: rota.id, substituiu: false, totalKm: res.totalKm, duracaoMin: res.duracaoMin, fonte: res.fonte };
}

/**
 * Recalcula uma rota ainda NÃO INICIADA (planejada/agendada/reagendada): reotimiza e
 * substitui as paradas. Guard otimista em `gerada_em` evita duas sessões ao mesmo tempo.
 */
export async function recalcularRotaPlanejada(args: {
    rotaId: string;
    pontos: PontoRoteirizavel[];
    origin?: Origem;
    geradaEmLida?: string | null;
}): Promise<{ ok: boolean; totalKm: number; duracaoMin: number | null; fonte: 'google' | 'local' }> {
    const { rotaId, pontos, origin, geradaEmLida } = args;

    // Trava otimista: só prossegue se `gerada_em` continua igual ao valor lido.
    const agora = new Date().toISOString();
    let claim = supabase.from('rotas_comerciais').update({ gerada_em: agora }).eq('id', rotaId).in('status', ['planejada', 'agendada', 'reagendada']);
    claim = geradaEmLida == null ? claim.is('gerada_em', null) : claim.eq('gerada_em', geradaEmLida);
    const { data: claimed, error: claimErr } = await claim.select('id');
    if (claimErr) throw claimErr;
    if (!claimed || claimed.length === 0) return { ok: false, totalKm: 0, duracaoMin: null, fonte: 'local' }; // outra sessão está recalculando

    const res = await otimizarPontos(pontos, origin, 'inteligente');

    // Lê a versão atual do snapshot para incrementar (auditoria).
    const { data: atual } = await supabase.from('rotas_comerciais').select('snapshot_otimizacao').eq('id', rotaId).maybeSingle();
    const versao = ((atual?.snapshot_otimizacao as { versao?: number } | null)?.versao || 1) + 1;

    await supabase.from('rota_paradas').delete().eq('rota_id', rotaId);
    await inserirParadas(rotaId, res.order);
    await supabase.from('rotas_comerciais').update({
        distancia_total_km: round1(res.totalKm),
        duracao_min: res.duracaoMin,
        snapshot_otimizacao: montarSnapshot(res, origin, versao),
    }).eq('id', rotaId);

    return { ok: true, totalKm: res.totalKm, duracaoMin: res.duracaoMin, fonte: res.fonte };
}

/**
 * Rota já em execução: anexa visitas novas SEM desmontar a rota. Nunca deleta paradas
 * (preservaria check-in/foto/OCR). Insere as novas ao fim e reordena só as 'pendente'.
 */
export async function anexarERecalcularPendentes(args: {
    rotaId: string;
    novosPontos: PontoRoteirizavel[];
    origin?: Origem;
}): Promise<void> {
    const { rotaId, novosPontos, origin } = args;
    if (novosPontos.length === 0) return;

    const { data: paradas } = await supabase
        .from('rota_paradas')
        .select('id, posicao, status, tarefa_id, negociacao_id, negociacoes(latitude, longitude, titulo)')
        .eq('rota_id', rotaId)
        .order('posicao', { ascending: true });

    const linhas = (paradas || []) as unknown as Array<{ id: string; posicao: number; status: string; tarefa_id: number | null; negociacao_id: number | null; negociacoes?: { latitude?: number | null; longitude?: number | null; titulo?: string | null } | null }>;

    const jaTem = new Set(linhas.map(p => p.tarefa_id).filter(Boolean));
    const aAnexar = novosPontos.filter(p => !jaTem.has(p.tarefa_id));
    if (aAnexar.length === 0) return;

    // Insere as novas ao fim, após a maior posição existente.
    const maxPos = linhas.reduce((m, p) => Math.max(m, p.posicao), 0);
    await supabase.from('rota_paradas').insert(
        aAnexar.map((p, i) => ({ rota_id: rotaId, posicao: maxPos + i + 1, negociacao_id: p.negociacao_id, tarefa_id: p.tarefa_id, modalidade: 'presencial' })),
    );

    // Reordena apenas as paradas ainda pendentes (as novas + as não visitadas), preservando
    // as já concluídas/em visita no início.
    await reordenarPendentesAPartirDe(rotaId, origin);
}

/**
 * Reordena SÓ as paradas pendentes de uma rota a partir de um ponto (ex.: GPS atual do
 * operador no meio do dia), preservando as já visitadas/em visita/puladas no início.
 * Pendentes sem geolocalização vão para o fim. Retorna quantas pendentes foram reordenadas.
 */
export async function reordenarPendentesAPartirDe(rotaId: string, origin?: Origem): Promise<number> {
    const { data } = await supabase
        .from('rota_paradas')
        .select('id, posicao, status, negociacoes(latitude, longitude)')
        .eq('rota_id', rotaId)
        .order('posicao', { ascending: true });
    const todas = (data || []) as unknown as Array<{ id: string; posicao: number; status: string; negociacoes?: { latitude?: number | null; longitude?: number | null } | null }>;

    const fixas = todas.filter(p => p.status !== 'pendente');
    const pendGeo = todas.filter(p => p.status === 'pendente' && p.negociacoes?.latitude && p.negociacoes?.longitude);
    const pendSemGeo = todas.filter(p => p.status === 'pendente' && !(p.negociacoes?.latitude && p.negociacoes?.longitude));
    if (pendGeo.length <= 1) return 0;

    const r = optimizeRoute(
        pendGeo.map(p => ({ id: p.id, lat: p.negociacoes!.latitude!, lng: p.negociacoes!.longitude!, label: '' })),
        origin,
    );

    // Renumera a rota inteira em sequência (fixas mantêm a ordem atual no início) e
    // grava só o que mudou, em lotes — numa rota de 100+ paradas, updates um a um
    // deixariam o botão pendurado por vários segundos.
    const posAtual = new Map(todas.map(p => [p.id, p.posicao]));
    const sequencia = [...fixas.map(p => p.id), ...r.order.map(p => p.id), ...pendSemGeo.map(p => p.id)];
    const mudadas = sequencia
        .map((id, i) => ({ id, posicao: i + 1 }))
        .filter(u => posAtual.get(u.id) !== u.posicao);
    for (let i = 0; i < mudadas.length; i += 20) {
        await Promise.all(mudadas.slice(i, i + 20).map(u =>
            supabase.from('rota_paradas').update({ posicao: u.posicao }).eq('id', u.id)
        ));
    }
    return r.order.length;
}

export { googleMapsDirLink, googleMapsNavUrl, GOOGLE_MAPS_MAX_STOPS };
